<script>
  import { onMount } from "svelte";

  // Get the theme from localStorage to keep consistency with main site
  let theme = "dark";
  if (typeof window !== "undefined" && window.localStorage) {
    theme = localStorage.theme || "dark";
  }

  function navigateToPage(url) {
    window.location.href = url;
  }

  // For the animated dots
  let dots = "";
  let dotCount = 0;
  let interval;

  onMount(() => {
    // Animate the dots
    interval = setInterval(() => {
      dotCount = (dotCount + 1) % 4;
      dots = ".".repeat(dotCount);
    }, 500);

    return () => {
      clearInterval(interval);
    };
  });
</script>

<main
  class="flex-1 overflow-hidden text-gray-800 dark:text-white min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-blue-900"
>
  <!-- Simple header -->
  <header class="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-md">
    <div class="container mx-auto px-4 py-4 flex items-center justify-center">
      <div class="text-lg font-semibold">Fit Log</div>
    </div>
  </header>

  <!-- Coming soon content -->
  <div
    class="container mx-auto px-4 flex flex-col items-center justify-center min-h-[80vh] text-center"
  >
    <!-- Blog section card -->
    <div
      class="bg-white/30 dark:bg-gray-800/20 backdrop-blur-xl border border-white/30 dark:border-gray-700/30 rounded-3xl p-6 md:p-8 w-full max-w-md cursor-pointer shadow-lg hover:scale-[1.02] hover:shadow-[0_20px_40px_-15px_rgba(59,130,246,0.15)] dark:hover:shadow-[0_20px_40px_rgba(59,130,246,0.15)] hover:border-blue-100/50 dark:hover:border-blue-500/30 transition-all duration-500 group mb-8"
      on:click={() => navigateToPage("/blog")}
      tabindex="0"
      on:keydown={(e) => {
        if (e.key === "Enter" || e.key === " ") navigateToPage("/blog");
      }}
      role="button"
      aria-label="Go to blog section"
    >
      <div class="flex items-center gap-4 mb-4">
        <svg
          class="w-10 h-10 text-blue-500 dark:text-blue-400"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
          ><path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z"
          /></svg
        >
        <div
          class="text-2xl font-bold text-blue-600 dark:text-blue-400 group-hover:underline"
        >
          Read our Blog
        </div>
      </div>
      <div class="text-gray-700 dark:text-gray-200 mb-2 line-clamp-2">
        Explore articles on building Fit Log, fitness tips, and more!
      </div>
      <div
        class="mt-2 text-blue-500 dark:text-blue-400 font-medium group-hover:underline flex items-center gap-1"
      >
        Go to Blog <svg
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
          ><path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M17 8l4 4m0 0l-4 4m4-4H3"
          /></svg
        >
      </div>
    </div>

    <div class="relative w-32 h-32 mb-8">
      <div
        class="absolute inset-0 rounded-full bg-blue-500/20 dark:bg-blue-500/10 animate-ping"
      ></div>
      <div
        class="absolute inset-3 rounded-full bg-blue-600/30 dark:bg-blue-600/20 animate-pulse"
      ></div>
      <div
        class="absolute inset-6 rounded-full bg-blue-700/40 dark:bg-blue-700/30 animate-pulse"
        style="animation-delay: 300ms"
      ></div>
      <div
        class="absolute inset-9 rounded-full bg-blue-800/50 dark:bg-blue-800/40 animate-pulse"
        style="animation-delay: 600ms"
      ></div>
      <div class="absolute inset-0 flex items-center justify-center">
        <svg
          class="w-16 h-16 text-blue-500 dark:text-blue-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
    </div>

    <h1
      class="text-5xl md:text-7xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-600"
    >
      Coming Soon
    </h1>

    <p
      class="text-xl md:text-2xl font-light mb-8 text-gray-600 dark:text-gray-300 animate-pulse"
    >
      Building an intelligent fitness experience with AI{dots}
    </p>
  </div>

  <!-- Animated background elements -->
  <div class="absolute inset-0 overflow-hidden pointer-events-none -z-10">
    {#each Array(5) as _, i}
      <div
        class="absolute rounded-full bg-blue-500/10 dark:bg-blue-400/5"
        style="
          width: {Math.random() * 300 + 50}px; 
          height: {Math.random() * 300 + 50}px; 
          left: {Math.random() * 100}%; 
          top: {Math.random() * 100}%; 
          animation: float {Math.random() * 15 + 10}s ease-in-out infinite;
          animation-delay: -{Math.random() * 5}s;
        "
      ></div>
    {/each}
  </div>
</main>

<style>
  @keyframes float {
    0%,
    100% {
      transform: translate(0, 0) rotate(0deg);
    }
    25% {
      transform: translate(-10px, -15px) rotate(5deg);
    }
    50% {
      transform: translate(10px, -30px) rotate(-5deg);
    }
    75% {
      transform: translate(-10px, -15px) rotate(3deg);
    }
  }
</style>
